import type { ReactNode } from 'react'
import { Descriptions, Drawer, Grid, Tooltip, Typography } from 'antd'
import { useTranslation } from 'react-i18next'
import { formatDateTime, utcTooltip } from '@/shared/lib/format'
import { formatMoneyValue } from '@/shared/lib/money'
import { KindText, ResultTag } from './OperationsList'
import { cashbackOf, type AgentOperation } from './profileApi'

interface OperationDetailProps {
  row: AgentOperation | null
  /**
   * SHU SAHIFADAGI qatorlar — cashback ular ORASIDAN topiladi
   * ({@link cashbackOf}). Alohida so'rov yo'q: server bunday endpoint
   * bermaydi, komissiya esa ro'yxatda o'z qatori bo'lib allaqachon bor.
   */
  rows: readonly AgentOperation[]
  /** `OperationsList` bilan AYNI funksiya — sahifa ikkalasiga beradi */
  planLabel?: (row: AgentOperation) => string | null
  onClose: () => void
}

/**
 * Bitta maydon: qiymat va (kerak bo'lsa) BO'SHLIGINING SABABI.
 *
 * ⚠️ Sabab qiymat O'RNIGA emas, ostiga yoziladi. «Noma'lum» deb yozish
 * yolg'on bo'lardi — maydon yo'qligining aniq va aytiladigan sababi bor.
 */
function Field({ value, note }: { value: ReactNode; note?: string }) {
  return (
    <>
      {value}
      {note ? (
        <Typography.Paragraph type="secondary" style={{ fontSize: 12, margin: '2px 0 0' }}>
          {note}
        </Typography.Paragraph>
      ) : null}
    </>
  )
}

/**
 * Bitta amalning batafsil ko'rinishi — egasi qarori (2026-08-19):
 * «to'lovlarning ustiga bosganda batafsil ma'lumot».
 *
 * <h2>⚠️ Telefonda PASTDAN, kompyuterda O'NGDAN</h2>
 * `SubscriberCardDrawer` bilan AYNI qaror va ayni sabab: bosh barmoq
 * yetadigan joy ekranning pastki uchdan biri, kompyuterda esa o'ng panel
 * ro'yxatni ko'rinib turgan holda qoldiradi.
 *
 * <h2>⚠️ BO'SH MAYDON — NOSOZLIK EMAS</h2>
 * Ro'yxatning manbai IKKITA (ledger ko'chirmasi va audit jurnali), ya'ni
 * ba'zi maydonlar tuzilmaviy sabab bilan yo'q va sabab AYTILADI:
 * <ul>
 *   <li>`subscriberLogin` — audit yozuvi 2026-08-18 dan oldin yozilgan
 *       bo'lsa yo'q: `audit_log` O'ZGARMAS, unga keyin `login` qo'shib
 *       bo'lmaydi. O'shanda `subscriberId` ko'rsatiladi, u ham bo'lmasa
 *       chiziqcha;</li>
 *   <li>`txnId` — `FAILED` urinishda tranzaksiya UMUMAN yaratilmagan,
 *       ya'ni raqam yo'q emas, o'zi mavjud emas;</li>
 *   <li>`reference` — depozit va naqd yozuvlarida idempotentlik kaliti
 *       tashqaridan kelmagan bo'lishi mumkin.</li>
 * </ul>
 *
 * <h2>⚠️ SUMMA ISHORASI SAQLANADI</h2>
 * `AgentOperationRow.amount` sherikning HISOBI nuqtai nazaridan yoziladi:
 * to'ldirish manfiy (depozitdan chiqdi), cashback va depozit to'ldirish
 * musbat. Server ishorani UI uchun ataylab teskari qilmaydi va panel ham
 * qilmaydi — «-30 000» aynan shu yerda pul chiqqanini ko'rsatadi.
 */
export function OperationDetail({ row, rows, planLabel, onClose }: OperationDetailProps) {
  const { t } = useTranslation()
  const screens = Grid.useBreakpoint()
  const isMobile = screens.md === false

  const cashback = row === null ? null : cashbackOf(rows, row)
  const failed = row?.result === 'FAILED'

  /**
   * ⚠️ Ro'yxatda chiziqcha turgan joyda BU YERDA `planId` ko'rsatiladi
   * va bu ziddiyat emas: ustunda id nomga o'xshab shovqin bo'lardi,
   * batafsil ko'rinishda esa u YAGONA ip — sherik shu satrni
   * administratorga aytadi va paket aynan shu bo'yicha topiladi.
   */
  const planShown = row === null ? null : (planLabel?.(row) ?? null)

  /**
   * Cashback yo'qligining SABABI — uchtasi boshqa-boshqa va bittasini
   * ikkinchisi o'rniga aytish yolg'on bo'lardi:
   *   · amal o'tmagan — komissiya hech qachon hisoblanmaydi;
   *   · to'ldirish emas — egasi qarori (2026-08-19): foiz FAQAT balans
   *     to'ldirishda, paket berishda YO'Q;
   *   · to'ldirish, lekin qatori yo'q — hali yozilmagan yoki boshqa
   *     sahifada ({@link cashbackOf}).
   */
  const cashbackNote =
    row === null || cashback !== null
      ? undefined
      : failed
        ? t('myOperations.detail.cashbackNone')
        : row.kind !== 'FUND'
          ? t('myOperations.detail.cashbackOnlyFund')
          : t('myOperations.detail.cashbackMissing')

  return (
    <Drawer
      open={row !== null}
      onClose={onClose}
      placement={isMobile ? 'bottom' : 'right'}
      {...(isMobile ? { height: '80%' } : { width: 460 })}
      title={t('myOperations.detail.title')}
      destroyOnHidden
    >
      {row === null ? null : (
        <Descriptions
          column={1}
          size="small"
          bordered
          items={[
            {
              key: 'at',
              label: t('myOperations.col.at'),
              // ⚠️ Vaqt TENANT zonasida; tooltip'da UTC ham — «nega
              // mendagi soat boshqa» savoli shu yerda yopiladi.
              // ⚠️ Tooltip ichida ELEMENT turadi, yalang'och satr emas:
              // antd bolaga `ref` bermoqchi bo'ladi va matn uni qabul
              // qilmaydi (konsolda ogohlantirish, tooltip esa ba'zan
              // umuman ochilmaydi).
              children: (
                <Tooltip title={utcTooltip(row.at)}>
                  <span>{formatDateTime(row.at)}</span>
                </Tooltip>
              ),
            },
            {
              key: 'subscriber',
              label: t('myOperations.col.subscriber'),
              children: (
                <Field
                  value={row.subscriberLogin ?? row.subscriberId ?? t('common.dash')}
                  note={
                    row.subscriberLogin === null
                      ? t('myOperations.detail.subscriberMissing')
                      : undefined
                  }
                />
              ),
            },
            { key: 'kind', label: t('myOperations.col.kind'), children: <KindText kind={row.kind} /> },
            // ⚠️ Qator faqat server paket haqida BIR NARSA aytganda
            // ochiladi: to'ldirish va komissiya yozuvlarida bo'sh
            // «Paket: —» qatori paket bo'lishi kerak edi degan yolg'on
            // taassurot berardi (aktor qatori bilan ayni qaror).
            ...(row.planId === null && row.planName === null
              ? []
              : [
                  {
                    key: 'plan',
                    label: t('myOperations.col.plan'),
                    children: (
                      <Field
                        value={planShown ?? row.planId ?? t('common.dash')}
                        note={planShown === null ? t('myOperations.detail.planMissing') : undefined}
                      />
                    ),
                  },
                ]),
            {
              key: 'amount',
              label: t('myOperations.col.amount'),
              children: (
                <Field
                  value={row.amount ? formatMoneyValue(row.amount) : t('common.dash')}
                  note={row.amount ? t('myOperations.detail.amountSign') : undefined}
                />
              ),
            },
            { key: 'result', label: t('myOperations.col.result'), children: <ResultTag result={row.result} /> },
            // ⚠️ Aktor faqat BOR bo'lganda qator ochadi: bo'sh «Kim: —»
            // qatori kim qilganini bilamiz, lekin aytmayapmiz degan
            // taassurot berardi. Yo'qligining sababi — eski yozuvda
            // aktor umuman saqlanmagani.
            ...(row.actor === null
              ? []
              : [{ key: 'actor', label: t('myOperations.col.actor'), children: row.actor }]),
            {
              key: 'cashback',
              label: t('myOperations.detail.cashback'),
              children: (
                <Field
                  value={cashback ? formatMoneyValue(cashback) : t('common.dash')}
                  note={cashbackNote}
                />
              ),
            },
            {
              key: 'txn',
              label: t('myOperations.detail.txnId'),
              children: (
                <Field
                  value={row.txnId ?? t('common.dash')}
                  note={
                    row.txnId === null && failed
                      ? t('myOperations.detail.txnMissing')
                      : undefined
                  }
                />
              ),
            },
            {
              key: 'reference',
              label: t('myOperations.detail.reference'),
              children: row.reference ? (
                // ⚠️ Kalit MATN bo'lib turadi: sherik uni administratorga
                // aytadi va aynan shu satr bo'yicha amal topiladi.
                <Typography.Text copyable style={{ fontSize: 12, wordBreak: 'break-all' }}>
                  {row.reference}
                </Typography.Text>
              ) : (
                t('common.dash')
              ),
            },
          ]}
        />
      )}
    </Drawer>
  )
}
